import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { financeApi } from '../../services/financeApi';
import { FinanceOverviewResponse } from '../../types/overview';
import PositionCards from '../../components/finance/PositionCards';
import FlowCards from '../../components/finance/FlowCards';
import { dubaiDate } from './PayoutsTab';

/** First day of the current Asia/Dubai month — the default flow range start (Flow C2: "month default"). */
export function dubaiMonthStart(now: Date = new Date()): string {
  return `${dubaiDate(0, now).slice(0, 7)}-01`;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Spec 053 Flow C2 / AC-28b — Finance → Overview (the first tab). Position cards as at a date
 * (default today, never before ledger go-live) and flow cards for a range (default the current
 * Dubai month). One fetch per filter change; the backend answers 200 with `notice` and no
 * `position` for a date before go-live, which is rendered inline (never a crash).
 */
export default function OverviewTab() {
  const auth = useAuth();
  const today = dubaiDate();
  const [asAt, setAsAt] = useState(today);
  const [from, setFrom] = useState(() => dubaiMonthStart());
  const [to, setTo] = useState(today);
  const [data, setData] = useState<FinanceOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportNote, setExportNote] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!auth.token) return;
    setLoading(true);
    setError(null);
    try {
      setData(await financeApi.getOverview({ asAt, from, to }, auth.token));
    } catch (err) {
      setData(null);
      setError(err instanceof Error ? err.message : 'Failed to load the overview');
    } finally {
      setLoading(false);
    }
  }, [auth.token, asAt, from, to]);

  useEffect(() => { load(); }, [load]);

  const runExport = async () => {
    if (!auth.token) return;
    setExporting(true);
    setExportNote(null);
    setExportError(null);
    try {
      const filename = await financeApi.downloadRangeExport('overview', from, to, auth.token);
      setExportNote(`Downloaded ${filename}`);
    } catch (err) {
      setExportError(err instanceof Error ? `Export failed: ${err.message}` : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const liveSince = data?.ledgerLiveSince ?? undefined;
  const onDate = (setter: (v: string) => void) => (value: string) => { if (DATE_RE.test(value)) setter(value); };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', fontSize: '0.8125rem' }} role="group" aria-label="Overview filters">
          <label htmlFor="overview-as-at" style={{ color: 'var(--admin-text-muted)' }}>Position as at</label>
          <input id="overview-as-at" type="date" className="admin-search-input" value={asAt} min={liveSince} max={today} disabled={loading}
            onChange={(e) => onDate(setAsAt)(e.target.value)} />
          <span style={{ color: 'var(--admin-border, #e5e7eb)' }}>|</span>
          <label htmlFor="overview-from" style={{ color: 'var(--admin-text-muted)' }}>Flows from</label>
          <input id="overview-from" type="date" className="admin-search-input" value={from} min={liveSince} max={to} disabled={loading}
            onChange={(e) => onDate(setFrom)(e.target.value)} />
          <label htmlFor="overview-to" style={{ color: 'var(--admin-text-muted)' }}>to</label>
          <input id="overview-to" type="date" className="admin-search-input" value={to} min={from} max={today} disabled={loading}
            onChange={(e) => onDate(setTo)(e.target.value)} />
          <span style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>Asia/Dubai</span>
        </div>
        <button
          type="button"
          className="admin-btn admin-btn-sm admin-btn-outline"
          disabled={!data?.position || exporting}
          onClick={runExport}
        >
          {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
      </div>

      {exportNote && <div className="admin-info-banner info" role="status">{exportNote}</div>}
      {exportError && <div className="admin-info-banner danger" role="alert" data-testid="export-error">{exportError}</div>}
      {error && <div className="admin-info-banner danger" role="alert">{error}</div>}

      {loading && !data ? (
        <div className="admin-loading"><div className="admin-loading-spinner"></div></div>
      ) : data && (
        <>
          {data.notice && (
            <div className="admin-info-banner warning" role="status" data-testid="overview-notice">{data.notice}</div>
          )}
          {data.warnings.map((w) => (
            <div key={w} className="admin-info-banner warning" role="status" data-testid="overview-warning">{w}</div>
          ))}

          <div className="admin-card" style={{ marginBottom: '1rem' }}>
            <div className="admin-card-header">
              <h3 className="admin-card-title">Position as at {data.asAt}</h3>
              <span style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>Ledger live since {data.ledgerLiveSince}</span>
            </div>
            {data.position
              ? <PositionCards position={data.position} asAt={data.asAt} links={data.links} />
              : <div className="admin-empty-state" data-testid="position-empty"><p>No position for this date.</p></div>}
          </div>

          <div className="admin-card">
            <div className="admin-card-header">
              <h3 className="admin-card-title">Flows {data.flows ? `${data.flows.from} → ${data.flows.to}` : `${from} → ${to}`}</h3>
              {to >= today && <span className="admin-badge pending" data-testid="flows-provisional">provisional — period still open</span>}
            </div>
            {data.flows
              ? <FlowCards flows={data.flows} links={data.links} />
              : <div className="admin-empty-state" data-testid="flows-empty"><p>No flows for this range.</p></div>}
          </div>
        </>
      )}
    </div>
  );
}
